<template>
  <AdaptiveSheet
    class="authLoginScreen authMethodPicker"
    :opened="isOpen"
    :close-by-backdrop-click="false"
    :close-by-outside-click="false"
    tablet-fullscreen
    swipe-to-close
    :backdrop="false"
    full-screen
    style="min-height: 100%; --f7-popup-bg-color: #fff"
    @close="onPopupClose"
  >
    <f7-view>
      <f7-page>
       <AppLayout>
        <template #main>
            <f7-login-screen-title>{{ $t("auth.methodPickerTitle") }}</f7-login-screen-title>
            <f7-block-header class="text-align-center">{{ reasonText }}</f7-block-header>

            <f7-list media-list strong class="method-list" v-if="methodList.length > 0">
              <f7-list-item link="#"
                v-for="method in methodList"
                :title="method.title"
                :subtitle="method.subtitle"
                :key="method.id"
                class="method-card"
                @click="choose(method.id)"
                role="button"
                tabindex="0"
                @keydown.enter="choose(method.id)"
                @keydown.space.prevent="choose(method.id)"
              >
                <template #media class="method-icon">
                  <f7-icon v-if="method.f7" :f7="method.f7" :size="24" />
                  <f7-icon v-else :material="method.material" :size="24" />
                </template>
                <template #after class="method-chevron">
                  <f7-icon f7="chevron_right" size="20" />
                </template>
              </f7-list-item>
            </f7-list>
              <f7-button large round fill raised class="margin-top" @click="cancel">{{ $t("common.cancel") }}</f7-button>          
        </template>
        </AppLayout>
      </f7-page>
    </f7-view>
  </AdaptiveSheet>
</template>

<style scoped>
.method-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 16px 0;
  max-width: 480px;
  margin: 0 auto;
}

.method-card {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid var(--f7-page-master-border-color, rgba(0, 0, 0, 0.1));
  background: var(--f7-list-item-background-color, #fff);
  color: var(--f7-text-color, #000);
  text-align: left;
  font-family: var(--f7-font-family);
  text-decoration: none;
}

.method-card:active {
  background: var(--f7-list-item-pressed-bg-color, rgba(0, 0, 0, 0.05));
}

.method-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--f7-list-item-pressed-bg-color, rgba(0, 0, 0, 0.05));
  color: var(--f7-theme-color, #0009ff);
}

.method-icon :deep(.f7-icon) {
  font-size: 24px;
}

.method-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.method-title {
  font-size: 16px;
  font-weight: 600;
}

.method-subtitle {
  font-size: 13px;
  margin-top: 2px;
  color: var(--f7-list-item-subtitle-text-color, rgba(0, 0, 0, 0.55));
}

.method-chevron {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.3);
}

.method-chevron :deep(.f7-icon) {
  font-size: 20px;
}
</style>

<script setup>
import { computed } from "vue";
import AdaptiveSheet from "./common/AdaptiveSheet.vue";
import i18n from "@/js/i18n.js";
import { useAuthMethodPicker } from "@/js/composables/useAuthMethodPicker.js";
import AppLayout from "./common/AppLayout.vue"; 

const { isOpen, options, choose, cancel } = useAuthMethodPicker();

const t = i18n.global.t;

const reasonText = computed(() => options.value.reason || t("auth.methodPickerHint"));

const methodList = computed(() => {
  const list = [];
  if (options.value.methods.includes("biometric")) {
    list.push({
      id: "biometric",
      title: options.value.biometryLabel || t("auth.biometric"),
      subtitle: t("auth.biometricHint"),
      f7: "fingerprint",
    });
  }
  if (options.value.methods.includes("passkey")) {
    list.push({
      id: "passkey",
      title: t("auth.anotherDevice"),
      subtitle: t("auth.passkeyHint"),
      f7: "devices",
    });
  }
  if (options.value.methods.includes("pin")) {
    list.push({
      id: "pin",
      title: t("auth.usePIN"),
      subtitle: t("auth.pinHint"),
      material: "lock",
    });
  }
  if (options.value.methods.includes("password")) {
    list.push({
      id: "password",
      title: t("auth.passwordSignIn"),
      subtitle: t("auth.passwordHint"),
      material: "key",
    });
  }
  return list;
});

function onPopupClose() {
  cancel();
}
</script>